import type { TimestampLike } from '../firestore/timestamp';

export type SaunaPhase = 'idle' | 'warming' | 'ready' | 'inUse' | 'cooldown' | 'stale';

export type SaunaFreshness = 'fresh' | 'stale' | 'unknown';

export type SaunaEstimateSource = 'live' | null;

export type SaunaSessionStartedBy = 'dashboard' | 'auto';

export type SaunaSessionStatus = 'warming' | 'ready' | 'inUse' | 'cooldown' | 'done' | 'aborted';

export type SaunaEventType = 'SESSION_STARTED' | 'READY' | 'IN_USE' | 'DONE' | 'COOLDOWN' | 'STALE_DETECTED' | 'ABORTED';

export type SaunaEventSource = 'state-machine' | 'manual';

export interface SaunaAutoStartConfig {
  enabled: boolean;
  minTemperatureC: number;
  windowMinutes: number;
  minRiseC: number;
  minHeatingRateCPerMin: number;
}

export interface SaunaConfig {
  sensorId: string | null;
  targetTemperatureC: number;
  readyStableSeconds: number;
  resetThresholdC: number;
  autoStart: SaunaAutoStartConfig;
}

export interface SaunaCurrentState<TTimestamp = TimestampLike> {
  phase: SaunaPhase;
  sessionId: string | null;
  currentTemperatureC: number | null;
  startTemperatureC: number | null;
  /** Snapshot of `config.targetTemperatureC` at session start. */
  targetTemperatureC: number;
  heatingDetected: boolean;
  heatingRateCPerMin: number | null;
  estimatedReadyAt: TTimestamp | null;
  estimateSource: SaunaEstimateSource;
  readyCandidateSince: TTimestamp | null;
  readySince: TTimestamp | null;
  lastTransitionAt: TTimestamp | null;
  updatedAt: TTimestamp;
}

export interface SaunaDoc<TTimestamp = TimestampLike> {
  config: SaunaConfig;
  currentState: SaunaCurrentState<TTimestamp>;
  createdAt: TTimestamp;
  updatedAt: TTimestamp;
}

export interface SaunaSessionDoc<TTimestamp = TimestampLike> {
  startedAt: TTimestamp;
  startedBy: SaunaSessionStartedBy;
  endedAt: TTimestamp | null;
  readyAt: TTimestamp | null;
  startTemperatureC: number | null;
  targetTemperatureC: number;
  maxTemperatureC: number | null;
  status: SaunaSessionStatus;
  /**
   * Observed time of the first reading at or above the target. Excludes the
   * `readyStableSeconds` hold. Absent on sessions recorded before this field
   * existed; readers approximate those as `readyAt - readyStableSeconds`.
   */
  targetReachedAt?: TTimestamp | null;
  /** Written on warming→ready and refreshed on done; enables Phase 12 historical fallback. Includes the ready hold. */
  minutesToReady: number | null;
  averageHeatingRateCPerMin: number | null;
}

/** Setup note doc at `homes/{homeId}/sauna/state/setupNotes/{noteId}`. */
export interface SaunaSetupNoteDoc<TTimestamp = TimestampLike> {
  text: string;
  /** When the setup change happened. Defaults to creation time; can be backdated. */
  notedAt: TTimestamp;
  createdAt: TTimestamp;
  updatedAt: TTimestamp;
}

export interface SaunaEventDoc<TTimestamp = TimestampLike> {
  type: SaunaEventType;
  sessionId: string | null;
  sensorId: string | null;
  temperatureC: number | null;
  createdAt: TTimestamp;
  source: SaunaEventSource;
}

export const SAUNA_STATE_DOC_ID = 'state';

export const SAUNA_TARGET_MIN_C = 30;
export const SAUNA_TARGET_MAX_C = 110;
export const SAUNA_READY_STABLE_MIN_S = 10;
export const SAUNA_READY_STABLE_MAX_S = 1800;
export const SAUNA_RESET_MIN_C = 20;
export const SAUNA_RESET_MAX_C = 100;

export const DEFAULT_SAUNA_TARGET_TEMPERATURE_C = 60;
export const DEFAULT_SAUNA_READY_STABLE_SECONDS = 120;
export const DEFAULT_SAUNA_RESET_THRESHOLD_C = 55;

/** Session history window for `getSaunaSessionHistoryCallable`. Independent of the widget's `historyDays`. */
export const SAUNA_SESSION_HISTORY_DAYS = 90;
export const SAUNA_SESSION_HISTORY_MAX_SESSIONS = 100;
/** Heating rate ignores the climb until this fraction of the start→target rise has been reached. */
export const SAUNA_HEATING_RATE_RISE_FRACTION = 0.25;
/** Heating rates computed from fewer sample intervals than this are flagged low-confidence. */
export const SAUNA_HEATING_RATE_MIN_CONFIDENT_INTERVALS = 6;
export const SAUNA_SETUP_NOTE_MAX_LENGTH = 500;
export const SAUNA_SETUP_NOTES_MAX = 200;

export const DEFAULT_SAUNA_AUTO_START: SaunaAutoStartConfig = {
  enabled: false,
  minTemperatureC: 40,
  windowMinutes: 10,
  minRiseC: 5,
  minHeatingRateCPerMin: 0.5,
};

// ─── Callable contracts ──────────────────────────────────────────────────────

/** `getSaunaStateCallable` current state: Timestamps are ISO strings on the wire. */
export interface SaunaCurrentStateView extends Omit<
  SaunaCurrentState,
  'estimatedReadyAt' | 'readyCandidateSince' | 'readySince' | 'lastTransitionAt' | 'updatedAt'
> {
  estimatedReadyAt: string | null;
  readyCandidateSince: string | null;
  readySince: string | null;
  lastTransitionAt: string | null;
  updatedAt: string;
  freshness: SaunaFreshness;
}

/** `getSaunaStateCallable` result. */
export interface SaunaStateView {
  exists: boolean;
  sensorId: string | null;
  config: SaunaConfig | null;
  currentState: SaunaCurrentStateView | null;
}

export interface SaunaHistoryPoint {
  observedAt: string;
  temperatureC: number | null;
  humidityPct: number | null;
}

/** `getSaunaHistoryCallable` result. */
export interface SaunaHistoryResponse {
  generatedAt: string;
  sensorId: string | null;
  points: SaunaHistoryPoint[];
}

/** Result of the manual sauna command callables (start / finish / acknowledge / abort). */
export interface SaunaCommandResult {
  phase: string;
  noop: boolean;
}

/** One row of `getSaunaSessionHistoryCallable`. Timestamps are ISO strings. */
export interface SaunaSessionSummary {
  sessionId: string;
  startedAt: string;
  endedAt: string | null;
  startedBy: SaunaSessionStartedBy;
  status: SaunaSessionStatus;
  startTemperatureC: number | null;
  maxTemperatureC: number | null;
  targetTemperatureC: number;
  targetReachedAt: string | null;
  /** True when `targetReachedAt` was derived from `readyAt - readyStableSeconds` (older sessions). */
  targetReachedAtApproximate: boolean;
  /** Not aborted and the target was reached. Only completed sessions have heating metrics. */
  completed: boolean;
  /** Minutes from start until the target was first reached. */
  heatingTimeMinutes: number | null;
  /** Median °C/min between consecutive samples from 25 % of the rise until the target was reached. */
  heatingRateCPerMin: number | null;
  /** Number of sample intervals the heating rate was computed from. */
  heatingRateIntervalCount: number;
  heatingRateLowConfidence: boolean;
}

export interface SaunaSetupNoteView {
  noteId: string;
  text: string;
  notedAt: string;
  createdAt: string;
  updatedAt: string;
}

/** `getSaunaSessionHistoryCallable` result. */
export interface SaunaSessionHistoryResponse {
  generatedAt: string;
  windowDays: number;
  /** Newest first. */
  sessions: SaunaSessionSummary[];
  /** True when the window held more sessions than were returned. */
  truncated: boolean;
  /** Newest first. */
  setupNotes: SaunaSetupNoteView[];
}

export interface CreateSaunaSetupNotePayload {
  homeId: string;
  text: string;
  /** ISO timestamp; defaults to now. */
  notedAt?: string;
}

export interface UpdateSaunaSetupNotePayload {
  homeId: string;
  noteId: string;
  text?: string;
  notedAt?: string;
}

export interface DeleteSaunaSetupNotePayload {
  homeId: string;
  noteId: string;
}

export interface UpdateSaunaConfigPayload {
  homeId: string;
  sensorId?: string | null;
  targetTemperatureC?: number;
  readyStableSeconds?: number;
  resetThresholdC?: number;
  autoStart?: Partial<SaunaAutoStartConfig>;
}
